import { For, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import {
  type ApiBlocker,
  BLOCKER_PAGE_MAX,
  type BlockerFocus,
  type BlockerReply,
} from '../lib/blockers';
import type { KeyRow } from '../lib/keys-types';
import { BlockerCard } from './BlockerCard';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';

// What the reader actually gets handed: every open ask, or the ONE the fleet
// notification deep-linked to. Presenter — `focusBlockers` decided which.
//
// A NAMED ID THAT IS NOT IN THE LIST GETS ITS OWN STATE. Someone arriving from a
// notification about one blocker most often finds it already answered, withdrawn
// or expired — closing a blocker takes it out of the OPEN list, it does not
// delete it. Rendering that as an empty page would read as a dead link; this says
// what probably happened and offers the one read that would find it.
//
// THE HEAD IS TRUNCATABLE and the footer says so. `GET /v1/blockers` serves
// `per_page` rows and mints no cursor, so there is no Load-more to offer — a
// button promising pages the server has no token for is a lie about the data.

/** The page's list of blockers: all of them, or the one a deep link focused. */
export function BlockerFeed(props: {
  readonly focus: BlockerFocus;
  readonly isBusy: (id: string) => boolean;
  /** The account's keys, threaded to each card's answer form (#6521). */
  readonly keys?: readonly KeyRow[] | undefined;
  readonly onAnswer: (blocker: ApiBlocker, reply: BlockerReply) => void;
  readonly onWithdraw: (blocker: ApiBlocker) => void;
  /** Widen to the closed record, or `null` when it is already being shown. */
  readonly onWiden: (() => void) | null;
}): JSX.Element {
  const rows = (): readonly ApiBlocker[] =>
    props.focus.kind === 'not_here' ? [] : props.focus.rows;

  return (
    <Switch>
      <Match when={props.focus.kind === 'not_here'}>
        <Panel title={t('blockers.not_here_title')}>
          <EmptyState
            message={t('blockers.not_here_desc')}
            {...(props.onWiden === null
              ? {}
              : { ctaLabel: t('blockers.not_here_widen'), onCta: props.onWiden })}
          />
        </Panel>
      </Match>
      <Match when={props.focus.kind !== 'not_here'}>
        <For each={rows()}>
          {(blocker) => (
            <BlockerCard
              blocker={blocker}
              busy={props.isBusy(blocker.id)}
              keys={props.keys}
              onAnswer={props.onAnswer}
              onWithdraw={props.onWithdraw}
            />
          )}
        </For>
        <Show when={rows().length === BLOCKER_PAGE_MAX}>
          <p class="card__desc">{t('blockers.truncated', { max: String(BLOCKER_PAGE_MAX) })}</p>
        </Show>
      </Match>
    </Switch>
  );
}
